<template>
  <div>

    <button
      v-if="hideDragDrop && props.attachments.length < props.maxFiles"
      class="flex items-center gap-2 text-bdBlue-400 hover:text-bdBlue-700 mx-auto px-4 py-2.5 rounded-lg bg-bdBlue-50 hover:bg-bdBlue-100 focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"
      @click="triggerClickInput"
    >
      <FontAwesomeIcon
        :icon="faPlusCircle"
      />
      <span>Upload More Files</span>
    </button>

    <div
      v-show="!hideDragDrop"
      class="relative flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed group"
      :data-active="active ? 'true' : 'false'"
      :class="[sizes.container, dragZoneClasses]"
      :style="dragZoneStyles"
      @dragenter.prevent="setActive"
      @dragover.prevent="setActive"
      @dragleave.prevent="setInactive"
      @drop.prevent="onDrop"
    >

      <img
        v-if="!hideUploadImage"
        :src="mimeImage.path"
        :alt="mimeImage.alt"
        :width="mimeImage.width"
        :height="mimeImage.height"
        :class="sizes.image"
        class="w-auto select-none"
      />
      <button
        type="button"
        class="font-bold text-center text-slate-900 block"
        :class="sizes.title"
        @click.prevent="() => emits('show-file-library')"
      >
        {{ title }}
      </button>

      <button
        v-if="props.description || props.maxFileSizeBytes"
        :class="sizes.description"
        class="text-center text-slate-400 font-medium block"
        @click.prevent="() => emits('show-file-library')"
      >{{ props.description }}{{props.description && maxFileSizeReadable ? ' • ' : ''}}{{maxFileSizeReadable}}</button>

      <div
        :class="sizes.buttonContainer"
        class="flex flex-col sm:flex-row items-center">

        <FormButton
          v-if="props.showFileLibrary"
          flex
          color="secondary-dark"
          class="w-64 sm:w-auto focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"
          medium
          :size="sizes.button"
          @click.prevent="() => emits('show-file-library')"
        >
          Add from Library
        </FormButton>

        <FormButton
          flex
          color="outline"
          class="w-64 sm:w-auto focus:outline-none focus:ring ring-offset-2 ring-bdBlue-200"
          :size="sizes.button"
          @click.prevent="triggerClickInput"
        >
          Upload {{ props.maxFiles === 1 ? "File" : "Files" }}
        </FormButton>
      </div>

      <input
        type="file"
        :accept="props.accept"
        :multiple="props.maxFiles > 1"
        ref="input"
        hidden
        @change="onFileChange"
        @click="resetEventTarget"
      />

    </div>

    <div
      class="space-y-4 mt-4"
    >
      <FileAttachment
        v-for="(attachment, index) in props.attachments"
        :key="`${attachment.original_path}-${attachment.order}`"
        :model-value="attachment"
        :show-file-library="props.showFileLibrary"
        :total="props.attachments.length || 0"
        @update:model-value="(e) => updateAttachments(index, e)"
        @switch-order="(newIndex) => switchOrder(index, newIndex)"
        @delete="() => deleteAttachment(index)"
      />
    </div>

  </div>
</template>

<script setup>
import FileAttachment from "./FileAttachment.vue";
import FormButton from "./FormButton.vue";
import {computed, onMounted, onUnmounted, ref, toRaw} from "vue";
import {getMimeImage} from "../constants/MimeImages.js";
import {FILE_ATTACHMENT_PROPS, fileToAttachment} from "../constants/FileLibrary.js";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {faPlusCircle} from "@fortawesome/free-solid-svg-icons";

const EVENTS_TO_HANDLE = ["dragenter", "dragover", "dragleave", "drop"];

const props = defineProps({
  ...FILE_ATTACHMENT_PROPS,
  isSmall: {
    type: Boolean,
    default: false,
  },
  hideUploadImage: {
    type: Boolean,
    default: false,
  },
  dragZoneClasses: {
    type: String,
    default: "",
  },
  hideDragDrop: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits([
  "update:attachments",
  "show-file-library",
]);

const mimeImage = getMimeImage(props.accept);

const input = ref();
const active = ref(false);
let inActiveTimeout = null;

const setActive = () => {
  active.value = true;
  clearTimeout(inActiveTimeout);
};

const setInactive = () => {
  inActiveTimeout = setTimeout(() => {
    active.value = false;
  }, 50);
};

const switchOrder = (oldIndex, newIndex) => {
  if (oldIndex === newIndex) return;

  const newAttachments = toRaw(props.attachments);

  [
    newAttachments[oldIndex],
    newAttachments[newIndex]
  ] = [
    newAttachments[newIndex],
    newAttachments[oldIndex]
  ];

  newAttachments[oldIndex].order = oldIndex;
  newAttachments[newIndex].order = newIndex;

  newAttachments.sort((a, b) => a.order - b.order);

  emits("update:attachments", newAttachments);
};

const deleteAttachment = (index) => {
  let newAttachments = props.attachments.filter((_, i) => i !== index);

  newAttachments
    .sort((a, b) => a.order - b.order)
    .forEach((a, i) => a.order = i);

  emits("update:attachments", newAttachments);
};

const processFiles = (files) => {
  const newAttachments = [];

  for (let i = 0; i < files.length; i++) {
    if (props.maxFiles === 1 && i > 0) break;

    if (props.attachments.length + i >= props.maxFiles) {
      toast.warning("You have reached the maximum number of files allowed.");
      break;
    }

    const file = files[i];
    const order = props.attachments.length + i;

    if (!file) continue;
    else if (isAllowedMimeType(file.type)) newAttachments.push(fileToAttachment(file, order));
    else if (props.maxFiles === 1) toast.info("This file is not in a supported format.");
    else toast.info(`File #${i + 1} is not in a supported format.`);
  }

  setInactive();
  emits("update:attachments", [...props.attachments, ...newAttachments]);
};

const onFileChange = (e) => processFiles(e.target.files);

const onDrop = (e) => processFiles(e.dataTransfer.files);

const isAllowedMimeType = (mimeType) => {
  return props.accept.includes(mimeType);
};
const resetEventTarget = (e) => e.target.value = "";
const triggerClickInput = () => input.value.click();

const updateAttachments = (index, updatedAttachment) => {
  let newAttachments = [...props.attachments];
  newAttachments[index] = updatedAttachment;
  emits("update:attachments", newAttachments);
};

const dragZoneStyles = computed(() => {
  if (active.value) return "border-color:#3472C6;background-color:#DFE9F7;";
  return "border-color:rgb(203 213 225);";
});

const sizes = computed(() => {
  if (props.isSmall) {
    return {
      container: "p-4",
      image: "h-14",
      title: "text-base mt-2",
      description: "text-xs my-1",
      button: "xs",
      buttonContainer: "gap-2 mt-2",
    };
  }

  return {
    container: "px-8 py-12",
    image: "h-20",
    title: "text-base mt-3",
    description: "text-sm mt-1 mb-3",
    button: "sm",
    buttonContainer: "gap-3 mt-3",
  };
});

const title = computed(() => {
  if (props.title) return props.title;
  if (props.showFileLibrary) return "Drag and drop, upload, or add from your library";
  return "Drag and drop or upload";
});

const maxFileSizeReadable = computed(() => {
  if (props.maxFileSizeBytes) {
    const sizeInBytes = props.maxFileSizeBytes;
    const units = ["bytes", "KB", "MB", "GB"];
    let index = 0;
    let size = sizeInBytes;

    while (size >= 1024 && index < units.length - 1) {
      size /= 1024;
      index++;
    }

    return `Up to ${size.toFixed(1)} ${units[index]}` + (
      props.maxFiles === 1 ? "" : " per file"
    );
  }
  return "";
});

function preventDefaults(e) {
  e.preventDefault();
}

onMounted(() => {
  EVENTS_TO_HANDLE.forEach((eventName) => document.body.addEventListener(eventName, preventDefaults));
});

onUnmounted(() => {
  EVENTS_TO_HANDLE.forEach((eventName) => document.body.removeEventListener(eventName, preventDefaults));
});
</script>
